import React, { useState, useEffect, useCallback, useMemo } from 'react';
import masterService from '../../../services/masterService';
import profileService from '../../../services/profileService';
import packageService from '../../../services/packageService';
import MasterHeader from '../components/MasterHeader';
import ConfirmModal from '../components/ConfirmModal';

export const ItemWiseRateTab = ({ showToast, initialItemId = null, initialItemType = 'TEST' }) => {
  // Bifurcation Item Type: 'TEST' | 'PROFILE' | 'PACKAGE'
  const [itemType, setItemType] = useState(initialItemType || 'TEST');

  // Catalogs
  const [investigations, setInvestigations] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [packages, setPackages] = useState([]);
  const [catalogsLoading, setCatalogsLoading] = useState(false);

  // Selected Billing Item ID (from f_itemmaster.ItemID)
  const [selectedItemId, setSelectedItemId] = useState(initialItemId || null);

  // Filter & Search
  const [panelSearch, setPanelSearch] = useState('');
  const [panelRates, setPanelRates] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modified rows: { [panelId]: { panelId, rate, mrpRate, itemDisplayName, itemCode } }
  const [modifiedRows, setModifiedRows] = useState({});

  // Selection for bulk actions
  const [selectedPanelIds, setSelectedPanelIds] = useState(new Set());

  // Bulk rate inputs
  const [bulkRateInput, setBulkRateInput] = useState('');
  const [bulkDiscountInput, setBulkDiscountInput] = useState('');
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkActionType, setBulkActionType] = useState('fixed');
  const [saving, setSaving] = useState(false);

  // 1. Load All Catalogs on Mount
  useEffect(() => {
    const loadCatalogs = async () => {
      setCatalogsLoading(true);
      try {
        const [invRes, profRes, pkgRes] = await Promise.all([
          masterService.getInvestigations({ isActive: true, pageSize: 500 }),
          profileService.getProfiles({ isActive: true }),
          packageService.getPackages({ isActive: true })
        ]);

        const invList = invRes.success && invRes.data ? (invRes.data.items || invRes.data || []) : [];
        const profList = profRes.success && profRes.data ? (profRes.data || []) : [];
        const pkgList = pkgRes.success && pkgRes.data ? (pkgRes.data || []) : [];

        setInvestigations(invList);
        setProfiles(profList);
        setPackages(pkgList);

        // Auto-select first item of default itemType if none selected
        if (!selectedItemId) {
          if (initialItemType === 'PACKAGE' && pkgList.length > 0) {
            setSelectedItemId(pkgList[0].itemId || pkgList[0].packageId);
          } else if (initialItemType === 'PROFILE' && profList.length > 0) {
            setSelectedItemId(profList[0].itemId || profList[0].profileId);
          } else if (invList.length > 0) {
            setSelectedItemId(invList[0].itemId || invList[0].investigationId);
          }
        }
      } catch (err) {
        showToast?.('Failed to load item catalogs.', 'error');
      } finally {
        setCatalogsLoading(false);
      }
    };

    loadCatalogs();
  }, [showToast, initialItemType]);

  // When switching itemType tabs, auto-select the first item of that type if current selection doesn't match
  const handleItemTypeSwitch = (newType) => {
    if (Object.keys(modifiedRows).length > 0) {
      if (!window.confirm('You have unsaved rate changes for the current item. Switching categories will discard them. Continue?')) {
        return;
      }
    }
    setItemType(newType);
    setModifiedRows({});
    setSelectedPanelIds(new Set());

    if (newType === 'TEST' && investigations.length > 0) {
      setSelectedItemId(investigations[0].itemId || investigations[0].investigationId);
    } else if (newType === 'PROFILE' && profiles.length > 0) {
      setSelectedItemId(profiles[0].itemId || profiles[0].profileId);
    } else if (newType === 'PACKAGE' && packages.length > 0) {
      setSelectedItemId(packages[0].itemId || packages[0].packageId);
    }
  };

  // Active Catalog List based on selected itemType
  const currentCatalog = useMemo(() => {
    if (itemType === 'TEST') return investigations;
    if (itemType === 'PROFILE') return profiles;
    if (itemType === 'PACKAGE') return packages;
    return [];
  }, [itemType, investigations, profiles, packages]);

  // Selected item metadata object
  const currentItem = useMemo(() => {
    if (!selectedItemId) return null;
    const sId = Number(selectedItemId);
    if (itemType === 'TEST') {
      const inv = investigations.find(i => (i.itemId || i.investigationId) === sId);
      if (inv) {
        return {
          id: inv.itemId || inv.investigationId,
          code: inv.testCode,
          name: inv.name || inv.investigationName,
          category: inv.departmentName || 'Diagnostics',
          baseRate: inv.baseRate || 0,
          componentCount: 1,
          type: 'TEST'
        };
      }
    } else if (itemType === 'PROFILE') {
      const prof = profiles.find(p => (p.itemId || p.profileId) === sId);
      if (prof) {
        return {
          id: prof.itemId || prof.profileId,
          code: prof.profileCode,
          name: prof.profileName,
          category: prof.departmentName || 'Clinical Profiles',
          baseRate: prof.baseRate || 0,
          componentCount: prof.activeComponentCount || 0,
          type: 'PROFILE'
        };
      }
    } else if (itemType === 'PACKAGE') {
      const pkg = packages.find(p => (p.itemId || p.packageId) === sId);
      if (pkg) {
        return {
          id: pkg.itemId || pkg.packageId,
          code: pkg.packageCode,
          name: pkg.packageName,
          category: 'Health Packages',
          baseRate: pkg.baseRate || 0,
          componentCount: pkg.activeComponentCount || (pkg.testCount + pkg.profileCount) || 0,
          type: 'PACKAGE'
        };
      }
    }
    return null;
  }, [selectedItemId, itemType, investigations, profiles, packages]);

  // 2. Fetch Panel Rates for Selected Item
  const fetchItemPanelRates = useCallback(async () => {
    if (!selectedItemId) return;
    setLoading(true);
    try {
      const res = await masterService.getItemRates(selectedItemId, {
        search: panelSearch.trim() || undefined
      });
      if (res.success && res.data) {
        setPanelRates(res.data);
        setModifiedRows({});
        setSelectedPanelIds(new Set());
      }
    } catch (err) {
      showToast?.('Failed to load panel rates for selected item.', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedItemId, panelSearch, showToast]);

  useEffect(() => {
    fetchItemPanelRates();
  }, [fetchItemPanelRates]);

  // Handle single panel rate edit
  const handleRateChange = (row, newRateVal) => {
    const val = parseFloat(newRateVal);
    if (isNaN(val) || val < 0) return;

    setModifiedRows(prev => ({
      ...prev,
      [row.panelId]: {
        panelId: row.panelId,
        rate: val,
        mrpRate: row.mrpRate || currentItem?.baseRate || 0,
        itemDisplayName: row.itemDisplayName || currentItem?.name,
        itemCode: row.itemCode || currentItem?.code
      }
    }));
  };

  // Toggle selection
  const toggleSelectPanel = (panelId) => {
    setSelectedPanelIds(prev => {
      const next = new Set(prev);
      if (next.has(panelId)) next.delete(panelId);
      else next.add(panelId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedPanelIds.size === panelRates.length) {
      setSelectedPanelIds(new Set());
    } else {
      setSelectedPanelIds(new Set(panelRates.map(p => p.panelId)));
    }
  };

  // Apply Bulk Rates
  const handleApplyBulk = () => {
    if (selectedPanelIds.size === 0) return;

    if (bulkActionType === 'fixed') {
      const rate = parseFloat(bulkRateInput);
      if (isNaN(rate) || rate < 0) {
        showToast?.('Please enter a valid rate.', 'error');
        return;
      }
      setModifiedRows(prev => {
        const next = { ...prev };
        panelRates.forEach(p => {
          if (selectedPanelIds.has(p.panelId)) {
            next[p.panelId] = {
              panelId: p.panelId,
              rate: rate,
              mrpRate: p.mrpRate || currentItem?.baseRate || 0,
              itemDisplayName: p.itemDisplayName || currentItem?.name,
              itemCode: p.itemCode || currentItem?.code
            };
          }
        });
        return next;
      });
      showToast?.(`Applied ₹${rate} to ${selectedPanelIds.size} panels. Click 'Save Rates' to persist.`, 'info');
    } else if (bulkActionType === 'percent') {
      const pct = parseFloat(bulkDiscountInput);
      if (isNaN(pct) || pct < 0 || pct > 100) {
        showToast?.('Please enter discount percentage between 0 and 100.', 'error');
        return;
      }
      const baseMrp = currentItem?.baseRate || (panelRates[0]?.mrpRate || 0);
      const calculatedRate = Math.round(baseMrp * (1 - pct / 100));

      setModifiedRows(prev => {
        const next = { ...prev };
        panelRates.forEach(p => {
          if (selectedPanelIds.has(p.panelId)) {
            next[p.panelId] = {
              panelId: p.panelId,
              rate: Math.max(0, calculatedRate),
              mrpRate: p.mrpRate || currentItem?.baseRate || 0,
              itemDisplayName: p.itemDisplayName || currentItem?.name,
              itemCode: p.itemCode || currentItem?.code
            };
          }
        });
        return next;
      });
      showToast?.(`Applied ${pct}% discount (₹${calculatedRate}) to ${selectedPanelIds.size} panels.`, 'info');
    }
    setShowBulkConfirm(false);
  };

  // Save All Changes
  const handleSaveAll = async () => {
    const dirtyPanels = Object.values(modifiedRows);
    if (dirtyPanels.length === 0) {
      showToast?.('No changes to save.', 'info');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        itemId: Number(selectedItemId),
        panels: dirtyPanels
      };
      const res = await masterService.batchUpdateItemRates(selectedItemId, payload);
      if (res.success) {
        showToast?.(`Successfully updated rates across ${dirtyPanels.length} panels!`, 'success');
        await fetchItemPanelRates();
      } else {
        showToast?.(res.message || 'Failed to save panel rates.', 'error');
      }
    } catch (err) {
      showToast?.(err.response?.data?.message || 'Error saving panel rates.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const dirtyCount = Object.keys(modifiedRows).length;

  return (
    <div className="mst-tab-content">
      {/* Top Header */}
      <MasterHeader
        title="Item-Wise Rate List"
        subtitle="Manage and configure tariff rates for ONE Single Test, Clinical Profile, or Health Package across MULTIPLE cash billing panels."
        icon={
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="16" y1="13" x2="8" y2="13"/>
            <line x1="16" y1="17" x2="8" y2="17"/>
            <polyline points="10 9 9 9 8 9"/>
          </svg>
        }
        actions={
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {dirtyCount > 0 && (
              <span style={{ fontSize: '0.84rem', fontWeight: 700, color: '#f59e0b', backgroundColor: '#fef3c7', padding: '6px 12px', borderRadius: 20 }}>
                ● {dirtyCount} Unsaved Changes
              </span>
            )}
            <button
              type="button"
              className="mst-btn-secondary"
              onClick={fetchItemPanelRates}
              disabled={loading || saving}
            >
              ↻ Refresh
            </button>
            <button
              type="button"
              className="mst-btn-primary"
              onClick={handleSaveAll}
              disabled={dirtyCount === 0 || saving}
              style={{ minWidth: 140 }}
            >
              {saving ? 'Saving...' : `Save Rates (${dirtyCount})`}
            </button>
          </div>
        }
      />

      {/* Control Panel Bar */}
      <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
        
        {/* Item Type Bifurcation Tabs */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', paddingBottom: 14, marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginRight: 6 }}>
              Tariff Item Category:
            </span>
            <button
              type="button"
              onClick={() => handleItemTypeSwitch('TEST')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 16px',
                borderRadius: 8,
                fontSize: '0.88rem',
                fontWeight: itemType === 'TEST' ? 700 : 500,
                border: itemType === 'TEST' ? '1.5px solid #0284c7' : '1px solid #cbd5e1',
                backgroundColor: itemType === 'TEST' ? '#f0f9ff' : '#ffffff',
                color: itemType === 'TEST' ? '#0369a1' : '#475569',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              🧪 Single Tests
              <span style={{ fontSize: '0.72rem', backgroundColor: itemType === 'TEST' ? '#bae6fd' : '#f1f5f9', color: itemType === 'TEST' ? '#0369a1' : '#64748b', padding: '2px 7px', borderRadius: 10, fontWeight: 700 }}>
                {investigations.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => handleItemTypeSwitch('PROFILE')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 16px',
                borderRadius: 8,
                fontSize: '0.88rem',
                fontWeight: itemType === 'PROFILE' ? 700 : 500,
                border: itemType === 'PROFILE' ? '1.5px solid #9333ea' : '1px solid #cbd5e1',
                backgroundColor: itemType === 'PROFILE' ? '#faf5ff' : '#ffffff',
                color: itemType === 'PROFILE' ? '#7e22ce' : '#475569',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              📋 Clinical Profiles
              <span style={{ fontSize: '0.72rem', backgroundColor: itemType === 'PROFILE' ? '#e9d5ff' : '#f1f5f9', color: itemType === 'PROFILE' ? '#7e22ce' : '#64748b', padding: '2px 7px', borderRadius: 10, fontWeight: 700 }}>
                {profiles.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => handleItemTypeSwitch('PACKAGE')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 16px',
                borderRadius: 8,
                fontSize: '0.88rem',
                fontWeight: itemType === 'PACKAGE' ? 700 : 500,
                border: itemType === 'PACKAGE' ? '1.5px solid #059669' : '1px solid #cbd5e1',
                backgroundColor: itemType === 'PACKAGE' ? '#ecfdf5' : '#ffffff',
                color: itemType === 'PACKAGE' ? '#047857' : '#475569',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              📦 Health Packages
              <span style={{ fontSize: '0.72rem', backgroundColor: itemType === 'PACKAGE' ? '#a7f3d0' : '#f1f5f9', color: itemType === 'PACKAGE' ? '#047857' : '#64748b', padding: '2px 7px', borderRadius: 10, fontWeight: 700 }}>
                {packages.length}
              </span>
            </button>
          </div>

          <div style={{ fontSize: '0.82rem', color: '#64748b' }}>
            {itemType === 'TEST' && 'Bifurcated: Single Test Tariffs'}
            {itemType === 'PROFILE' && 'Bifurcated: Multi-Test Clinical Profiles'}
            {itemType === 'PACKAGE' && 'Bifurcated: Commercial Health Packages'}
          </div>
        </div>

        {/* Item Selector & Filters Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.2fr auto', gap: 16, alignItems: 'center' }}>
          {/* Dynamic Item Selector */}
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', color: '#475569', marginBottom: 4 }}>
              {itemType === 'TEST' && 'Select Single Test / Investigation'}
              {itemType === 'PROFILE' && 'Select Clinical Profile'}
              {itemType === 'PACKAGE' && 'Select Health Package'}
            </label>
            <select
              className="mst-form-select"
              value={selectedItemId || ''}
              onChange={(e) => {
                if (dirtyCount > 0 && !window.confirm('You have unsaved rate changes. Switching items will discard them. Continue?')) {
                  return;
                }
                setSelectedItemId(Number(e.target.value));
              }}
              disabled={catalogsLoading || loading}
              style={{ fontWeight: 600, fontSize: '0.92rem' }}
            >
              {currentCatalog.map(item => {
                const id = item.itemId || item.investigationId || item.profileId || item.packageId;
                if (itemType === 'TEST') {
                  return (
                    <option key={id} value={id}>
                      [{item.testCode || 'N/A'}] {item.name || item.investigationName} — {item.departmentName || 'Lab'} (MRP: ₹{(item.baseRate || 0).toFixed(2)})
                    </option>
                  );
                } else if (itemType === 'PROFILE') {
                  return (
                    <option key={id} value={id}>
                      [{item.profileCode || 'N/A'}] {item.profileName} — {item.departmentName || 'Profiles'} • {item.activeComponentCount || 0} tests
                    </option>
                  );
                } else {
                  return (
                    <option key={id} value={id}>
                      [{item.packageCode || 'N/A'}] {item.packageName} • {item.activeComponentCount || (item.testCount + item.profileCount) || 0} components
                    </option>
                  );
                }
              })}
            </select>
          </div>

          {/* Search Panels */}
          <div>
            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', color: '#475569', marginBottom: 4 }}>
              Filter Billing Panels
            </label>
            <input
              type="text"
              className="mst-form-input"
              placeholder="Search by panel name or code..."
              value={panelSearch}
              onChange={(e) => setPanelSearch(e.target.value)}
            />
          </div>

          {/* Item Context Pill */}
          <div style={{
            padding: '10px 18px',
            backgroundColor: itemType === 'PACKAGE' ? '#ecfdf5' : itemType === 'PROFILE' ? '#faf5ff' : '#f0fdf4',
            border: itemType === 'PACKAGE' ? '1px solid #a7f3d0' : itemType === 'PROFILE' ? '1px solid #e9d5ff' : '1px solid #bbf7d0',
            borderRadius: 8,
            textAlign: 'right'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, marginBottom: 2 }}>
              <span style={{
                fontSize: '0.7rem',
                fontWeight: 700,
                padding: '1px 6px',
                borderRadius: 4,
                backgroundColor: itemType === 'PACKAGE' ? '#059669' : itemType === 'PROFILE' ? '#7e22ce' : '#0284c7',
                color: '#ffffff'
              }}>
                {itemType}
              </span>
              <span style={{ fontSize: '0.75rem', color: '#475569', fontWeight: 600 }}>Standard MRP</span>
            </div>
            <div style={{ fontSize: '1.15rem', fontWeight: 800, color: itemType === 'PACKAGE' ? '#047857' : itemType === 'PROFILE' ? '#6b21a8' : '#15803d' }}>
              ₹{(currentItem?.baseRate || panelRates[0]?.mrpRate || 0).toFixed(2)}
            </div>
          </div>
        </div>

        {/* Bulk Action Strip */}
        {selectedPanelIds.size > 0 && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#f8fafc', padding: 12, borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1e293b' }}>
                ✓ {selectedPanelIds.size} panels selected
              </span>
              <button
                type="button"
                className="mst-btn-secondary"
                style={{ fontSize: '0.78rem', padding: '4px 8px' }}
                onClick={() => setSelectedPanelIds(new Set())}
              >
                Clear Selection
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '0.82rem', color: '#64748b' }}>Set Fixed Rate: ₹</span>
                <input
                  type="number"
                  placeholder="250"
                  style={{ width: 90, padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.88rem' }}
                  value={bulkRateInput}
                  onChange={(e) => setBulkRateInput(e.target.value)}
                />
                <button
                  type="button"
                  className="mst-btn-secondary"
                  style={{ fontSize: '0.82rem', padding: '5px 10px' }}
                  onClick={() => {
                    setBulkActionType('fixed');
                    setShowBulkConfirm(true);
                  }}
                  disabled={!bulkRateInput}
                >
                  Apply Rate
                </button>
              </div>

              <span style={{ color: '#cbd5e1' }}>|</span>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '0.82rem', color: '#64748b' }}>Discount:</span>
                <input
                  type="number"
                  placeholder="15"
                  style={{ width: 70, padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.88rem' }}
                  value={bulkDiscountInput}
                  onChange={(e) => setBulkDiscountInput(e.target.value)}
                />
                <span style={{ fontSize: '0.82rem', color: '#64748b' }}>% off MRP</span>
                <button
                  type="button"
                  className="mst-btn-secondary"
                  style={{ fontSize: '0.82rem', padding: '5px 10px' }}
                  onClick={() => {
                    setBulkActionType('percent');
                    setShowBulkConfirm(true);
                  }}
                  disabled={!bulkDiscountInput}
                >
                  Apply %
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Panels Rates Table */}
      <div style={{ backgroundColor: '#ffffff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
        <table className="mst-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <th style={{ width: 40, padding: '10px 14px', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={panelRates.length > 0 && selectedPanelIds.size === panelRates.length}
                  onChange={toggleSelectAll}
                />
              </th>
              <th style={{ width: 50, padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase' }}>#</th>
              <th style={{ width: 130, padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase' }}>Panel Code</th>
              <th style={{ padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase' }}>Panel Name</th>
              <th style={{ width: 220, padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase' }}>Diagnostic Centre</th>
              <th style={{ width: 130, padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', textAlign: 'right' }}>Standard MRP</th>
              <th style={{ width: 130, padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', textAlign: 'right' }}>Current Rate</th>
              <th style={{ width: 160, padding: '10px 14px', fontSize: '0.78rem', color: '#0f172a', textTransform: 'uppercase', textAlign: 'right' }}>New Rate (₹)</th>
              <th style={{ width: 90, padding: '10px 14px', fontSize: '0.78rem', color: '#64748b', textTransform: 'uppercase', textAlign: 'center' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                  <div style={{ display: 'inline-block', width: 24, height: 24, border: '3px solid #cbd5e1', borderTopColor: '#0284c7', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  <div style={{ marginTop: 8, fontSize: '0.88rem' }}>Loading panel schedules...</div>
                </td>
              </tr>
            ) : panelRates.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                  No billing panels found.
                </td>
              </tr>
            ) : (
              panelRates.map((p, idx) => {
                const isSelected = selectedPanelIds.has(p.panelId);
                const isModified = modifiedRows[p.panelId] !== undefined;
                const activeRate = isModified ? modifiedRows[p.panelId].rate : p.panelRate;

                return (
                  <tr
                    key={p.panelId}
                    style={{
                      borderBottom: '1px solid #f1f5f9',
                      backgroundColor: isModified ? '#fffbeb' : isSelected ? '#f0f9ff' : idx % 2 === 0 ? '#ffffff' : '#fafafa'
                    }}
                  >
                    <td style={{ textAlign: 'center', padding: '8px 14px' }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectPanel(p.panelId)}
                      />
                    </td>
                    <td style={{ padding: '8px 14px', color: '#94a3b8', fontSize: '0.82rem' }}>{idx + 1}</td>
                    <td style={{ padding: '8px 14px' }}>
                      <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#0f766e', backgroundColor: '#ccfbf1', padding: '2px 6px', borderRadius: 4, fontSize: '0.8rem' }}>
                        {p.panelCode || 'N/A'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 14px', fontWeight: 600, color: '#1e293b' }}>
                      {p.panelName}
                      {p.panelId === 1 && (
                        <span style={{ marginLeft: 8, fontSize: '0.72rem', backgroundColor: '#e2e8f0', color: '#475569', padding: '2px 6px', borderRadius: 4 }}>
                          Default Walk-In
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '8px 14px', fontSize: '0.84rem', color: '#475569' }}>
                      {p.centreName}
                    </td>
                    <td style={{ padding: '8px 14px', textAlign: 'right', fontSize: '0.88rem', color: '#64748b' }}>
                      ₹{p.mrpRate.toFixed(2)}
                    </td>
                    <td style={{ padding: '8px 14px', textAlign: 'right', fontSize: '0.88rem', fontWeight: 600, color: '#334155' }}>
                      ₹{p.panelRate.toFixed(2)}
                    </td>
                    <td style={{ padding: '8px 14px', textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>₹</span>
                        <input
                          type="number"
                          step="1"
                          min="0"
                          style={{
                            width: 100,
                            padding: '6px 8px',
                            textAlign: 'right',
                            fontWeight: 700,
                            fontSize: '0.92rem',
                            borderRadius: 6,
                            border: isModified ? '2px solid #f59e0b' : '1px solid #cbd5e1',
                            backgroundColor: isModified ? '#ffffff' : '#f8fafc',
                            color: isModified ? '#b45309' : '#0f172a'
                          }}
                          value={activeRate}
                          onChange={(e) => handleRateChange(p, e.target.value)}
                        />
                      </div>
                    </td>
                    <td style={{ padding: '8px 14px', textAlign: 'center' }}>
                      {isModified ? (
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 12, backgroundColor: '#fef3c7', color: '#d97706' }}>
                          DIRTY
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 12, backgroundColor: '#dcfce7', color: '#16a34a' }}>
                          SAVED
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Bulk Safety Confirmation */}
      <ConfirmModal
        isOpen={showBulkConfirm}
        title="Confirm Item-Wise Rate Update"
        message={
          bulkActionType === 'fixed'
            ? `You are about to update ${itemType.toLowerCase()} '${currentItem?.name}' to ₹${bulkRateInput} across ${selectedPanelIds.size} selected panels.`
            : `You are about to apply ${bulkDiscountInput}% discount off MRP across ${selectedPanelIds.size} selected panels for ${itemType.toLowerCase()} '${currentItem?.name}'.`
        }
        confirmLabel="Apply to Selected"
        cancelLabel="Cancel"
        onConfirm={handleApplyBulk}
        onCancel={() => setShowBulkConfirm(false)}
      />
    </div>
  );
};

export default ItemWiseRateTab;
